/**
 * Tool (SMSWiser) stats columns + adapter to the shared GenericStatsRow.
 * The native tool metrics differ from the Voluum tracker set, so we render the
 * same StatsTable with a tool-specific column list (metrics live in row.voluum,
 * which StatsTable treats as a generic key→number bag).
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  ColumnDef,
  GenericStatsRow,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import type {
  NormalizedStatsMetrics,
  NormalizedStatsRow,
  ToolStatsDimension,
} from '@/hooks/smswiser/tool-stats';

interface ToolColumn {
  key: string;
  label: string;
  format: 'int' | 'percent' | 'currency' | 'decimal';
  colorHint?: 'metric' | 'money' | 'negative';
  columnAccent?: 'conv' | 'reg' | 'ftd';
  /** Short explanation shown via an "i" hint on the column header. */
  tooltip?: string;
}

export const TOOL_COLUMNS: ToolColumn[] = [
  { key: 'sent', label: 'Sent', format: 'int', tooltip: 'Messages SMSWiser sent for this drop.' },
  {
    key: 'delivered',
    label: 'Delivered',
    format: 'int',
    tooltip: 'Delivery counts are only reported by SMSWiser for short-link rows, so this is blank (—) on other tabs. The per-message list (Show per-message detail) shows delivered / not-delivered for every message.',
  },
  {
    key: 'undeliverable',
    label: 'Undeliv.',
    format: 'int',
    colorHint: 'negative',
    tooltip: 'Undeliverable count — only reported for short-link rows; blank (—) elsewhere.',
  },
  { key: 'clicks', label: 'Clicks', format: 'int', tooltip: 'Tracked link clicks (from Binom click tracking on the short link).' },
  { key: 'bot', label: 'Bot', format: 'int', colorHint: 'negative', tooltip: 'Clicks classified as bots (link scanners), excluded from real CTR.' },
  { key: 'ctr', label: 'CTR', format: 'percent', colorHint: 'metric', tooltip: 'Click-through rate = clicks ÷ sent.' },
  {
    key: 'conversions',
    label: 'Conv.',
    format: 'int',
    colorHint: 'metric',
    columnAccent: 'conv',
    tooltip: 'Conversions. These arrive from Binom conversion postbacks — until those are wired up they stay 0.',
  },
  {
    key: 'revenue',
    label: 'Revenue',
    format: 'currency',
    colorHint: 'money',
    tooltip: 'Revenue reported back by Binom postbacks. 0 until conversion tracking is configured.',
  },
  {
    key: 'costs',
    label: 'Cost',
    format: 'currency',
    colorHint: 'negative',
    tooltip: 'Real send cost = gateway price per SMS × sent, reported by SMSWiser.',
  },
  {
    key: 'profit',
    label: 'Profit',
    format: 'currency',
    colorHint: 'money',
    tooltip: 'Profit = Revenue − Cost. Shows negative while revenue is still 0 (no conversion postbacks yet).',
  },
  {
    key: 'roi',
    label: 'ROI',
    format: 'percent',
    colorHint: 'money',
    tooltip: 'Return on investment = Profit ÷ Cost. −100% means cost with no tracked revenue yet.',
  },
  { key: 'epc', label: 'EPC', format: 'currency', colorHint: 'money', tooltip: 'Earnings per click = revenue ÷ clicks.' },
  { key: 'ttcAvg', label: 'TTC avg', format: 'decimal', tooltip: 'Average time-to-conversion (seconds) from click to conversion.' },
];

/** StatsTable's ColumnDef union is the narrow Voluum set; cast (same as VOLUUM_COLUMNS). */
export const TOOL_COLUMN_DEFS = TOOL_COLUMNS as unknown as ColumnDef[];

export const TOOL_DIMENSIONS: ReadonlyArray<{
  dimension: ToolStatsDimension;
  title: string;
  firstColumnLabel: string;
}> = [
  { dimension: 'sms', title: 'Delivery health', firstColumnLabel: 'Metric' },
  { dimension: 'abTesting', title: 'A/B testing', firstColumnLabel: 'Variant' },
  { dimension: 'batch', title: 'Batches', firstColumnLabel: 'Batch' },
  { dimension: 'shortlink', title: 'Short-link domains', firstColumnLabel: 'Domain' },
];

/** Flatten a metrics block into the generic key→number record StatsTable reads. */
export function toToolMetricRecord(m: NormalizedStatsMetrics): Record<string, number> {
  const rec: Record<string, number> = {
    sent: m.sent,
    clicks: m.clicks,
    bot: m.bot,
    ctr: m.ctr,
    epc: m.epc,
    revenue: m.revenue,
    conversions: m.conversions,
    costs: m.costs,
    profit: m.profit,
    roi: m.roi,
  };
  if (m.ttcAvg != null) rec.ttcAvg = m.ttcAvg;
  if (m.delivered != null) rec.delivered = m.delivered;
  if (m.undeliverable != null) rec.undeliverable = m.undeliverable;
  return rec;
}

export function toGenericStatsRowFromToolRow(row: NormalizedStatsRow): GenericStatsRow {
  return {
    id: row.id,
    label: row.label,
    subLabel: row.subLabel ?? undefined,
    voluum: toToolMetricRecord(row.metrics),
    searchText: `${row.label} ${row.subLabel ?? ''}`.trim().toLowerCase(),
  };
}

/**
 * Same adapter, but tags the row with its source drop (for the aggregated
 * "All drops" view) so it can be grouped by drop and stays id-unique.
 */
export function toGenericStatsRowWithDrop(
  row: NormalizedStatsRow,
  dropUniqueId: string,
  dropLabel: string,
): GenericStatsRow {
  const base = toGenericStatsRowFromToolRow(row);
  return {
    ...base,
    id: `${dropUniqueId}:${base.id}`,
    dropId: dropLabel,
    searchText: `${base.searchText} ${dropLabel}`.trim().toLowerCase(),
  };
}
